import React, { useState, useEffect } from 'react';
import { View, TouchableOpacity, Image, StyleSheet, Text } from 'react-native';
import { useTheme } from '../contexts/ThemeContext';
import UserMenu from './UserMenu';

export default function LandingStyleHeader({ navigation, currentScreen }) {
  const { colors, isDark, setTheme } = useTheme();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isVisible, setIsVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Detect mobile screen size
  useEffect(() => {
    const checkMobile = () => {
      const mobile = window.innerWidth < 1024;
      setIsMobile(mobile);
      if (!mobile) {
        setMobileMenuOpen(false);
      }
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Handle scroll for header behavior
  useEffect(() => {
    let ticking = false;
    
    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          // Check for scroll position from isolated ScrollViews or window scroll
          const scrollY = window.lastKnownScrollY !== undefined ? window.lastKnownScrollY : (window.scrollY || window.pageYOffset);
          
          // Show/hide based on scroll direction
          if (scrollY > lastScrollY && scrollY > 100) {
            // Scrolling down & past threshold - hide and STAY hidden
            setIsVisible(false);
          } else if (scrollY < lastScrollY) {
            // Scrolling up - show header
            setIsVisible(true);
          }
          // Don't change visibility if scrollY === lastScrollY (stopped scrolling)
          
          // Change style if scrolled past threshold
          setIsScrolled(scrollY > 50);
          setLastScrollY(scrollY);
          
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [lastScrollY]);

  const toggleTheme = () => {
    setTheme(isDark ? 'light' : 'dark');
  };

  return (
    <>
      {/* Smart Header with scroll behavior */}
      <View style={[
        styles.headerContainer,
        isMobile && styles.headerContainerMobile,
        isScrolled && styles.headerScrolled,
        // Glassmorphic background when scrolled
        isScrolled && !isMobile && { 
          backgroundColor: isDark ? 'rgba(26, 26, 26, 0.75)' : 'rgba(255, 255, 255, 0.75)',
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
          boxShadow: isDark 
            ? '0 8px 32px rgba(0, 0, 0, 0.5)' 
            : '0 8px 32px rgba(0, 0, 0, 0.15)',
        },
        // Mobile always has glassmorphic background
        isMobile && {
          backgroundColor: isDark ? 'rgba(26, 26, 26, 0.9)' : 'rgba(255, 255, 255, 0.9)',
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
        },
        // Transparent at top on desktop
        !isScrolled && !isMobile && { 
          backgroundColor: 'transparent',
          backdropFilter: 'none',
          WebkitBackdropFilter: 'none',
        },
        // Hide header when scrolled down
        !isVisible && !isMobile && styles.headerHidden,
      ]}>
        <View style={[styles.header, isMobile && styles.headerMobile]}>
          <TouchableOpacity 
            style={[styles.logoButton, isMobile && styles.logoButtonMobile]}
            onPress={() => navigation.navigate('dashboard')}
          >
            <Image 
              source={require('../assets/logo.png')} 
              style={[styles.logo, isMobile && styles.logoMobile]}
              resizeMode="contain"
            />
          </TouchableOpacity>
          
          {isMobile ? (
            <TouchableOpacity 
              style={[styles.hamburger, { borderColor: colors.border }]}
              onPress={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              <View style={[styles.hamburgerLine, { backgroundColor: colors.text }]} />
              <View style={[styles.hamburgerLine, { backgroundColor: colors.text }]} />
              <View style={[styles.hamburgerLine, { backgroundColor: colors.text }]} />
            </TouchableOpacity>
          ) : (
            <View style={styles.headerRight}>
              <TouchableOpacity 
                style={[
                  styles.themeToggle,
                  { backgroundColor: colors.secondary, borderColor: colors.border }
                ]}
                onPress={toggleTheme}
              >
                <Text style={[styles.themeToggleText, { color: colors.text }]}>
                  {isDark ? '☀️' : '🌙'}
                </Text>
              </TouchableOpacity>
              <UserMenu navigation={navigation} currentScreen={currentScreen} />
            </View>
          )}
        </View>
      </View>

      {/* Mobile Menu */}
      {isMobile && (
        <UserMenu 
          navigation={navigation} 
          currentScreen={currentScreen} 
          isMobile={true}
          externalControl={true}
          showMenu={mobileMenuOpen}
          onClose={() => setMobileMenuOpen(false)}
        />
      )}
    </>
  );
}

const styles = StyleSheet.create({
  headerContainer: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    zIndex: 2000,
    backgroundColor: 'transparent',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  },
  headerScrolled: {
    top: 16,
    left: 16,
    right: 16,
    borderRadius: 20,
  },
  headerHidden: {
    transform: 'translateY(-120%)',
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 32,
    paddingVertical: 12,
    position: 'relative',
  },
  logoButton: {
    padding: 4,
    zIndex: 2001,
  },
  logo: {
    width: 240,
    height: 68,
  },
  headerRight: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 16,
    zIndex: 2001,
  },
  themeToggle: {
    width: 44,
    height: 44,
    borderRadius: 12,
    borderWidth: 1,
    justifyContent: 'center',
    alignItems: 'center',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
  },
  themeToggleText: {
    fontSize: 20,
    userSelect: 'none',
  },

  // Mobile & Tablet Styles (< 1024px)
  headerContainerMobile: {
    borderRadius: 0,
    boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)',
  },
  headerMobile: {
    paddingHorizontal: 16,
    paddingVertical: 10,
  },
  logoButtonMobile: {
    padding: 2,
  },
  logoMobile: {
    width: 140,
    height: 40,
  },
  hamburger: {
    width: 44,
    height: 44,
    borderRadius: 12,
    borderWidth: 1,
    justifyContent: 'center',
    alignItems: 'center',
    gap: 6,
    padding: 10,
  },
  hamburgerLine: {
    width: 24,
    height: 2,
    borderRadius: 2,
  },
});

